/**
 * Animator: plays the base layer of an AnimatorController (sprite-swap clips only, which is all this
 * project uses), with parameters, triggers, exit-time transitions and Animator.Play.
 * AnimatedEffect: the one-shot effect object DynamicObject spawns on despawn.
 */
import type { AnimClipDef, AnimControllerDef, AnimStateDef, AnimTransitionDef } from '../../../model/gamedata.ts';
import { GameBehaviour, type Game } from '../game.ts';
import { compose } from '../../../model/xform.ts';
import { SimGo } from '../world.ts';
import type { V2 } from '../unity.ts';

const enum ParamType {
  Float = 1,
  Int = 3,
  Bool = 4,
  Trigger = 9,
}

const enum ConditionMode {
  If = 1,
  IfNot = 2,
  Greater = 3,
  Less = 4,
  Equals = 6,
  NotEqual = 7,
}

export class Animator extends GameBehaviour {
  ctrl: AnimControllerDef | null = null;
  state = '';
  /** seconds since the current state was entered (state speed applied) */
  time = 0;
  speed = 1;
  params: Record<string, number> = {};
  /** sprite curves can't blend: the previous state's frame is kept for half of the cross-fade */
  holdKey = '';
  holdFor = 0;
  override noSave = ['ctrl'];

  override awake() {
    const guid = this.data?.m_Controller?.guid;
    this.setController(guid ? String(guid) : null);
  }

  override start() {
    this.apply();
  }

  /** runtimeAnimatorController = ...; Rebind() */
  setController(guid: string | null) {
    this.ctrl = guid ? this.game.gd.animations?.controllers[guid] ?? null : null;
    this.params = {};
    for (const p of this.ctrl?.params ?? []) this.params[p.name] = p.type === ParamType.Trigger ? 0 : p.value;
    this.enter(this.ctrl?.defaultState ?? '', 0);
  }

  get stateDef(): AnimStateDef | null {
    return this.ctrl?.states[this.state] ?? null;
  }

  get clip(): AnimClipDef | null {
    const c = this.stateDef?.clip;
    return c ? this.game.gd.animations?.clips[c] ?? null : null;
  }

  /** AnimatorStateInfo.normalizedTime (not wrapped: 2.5 = half-way through the third loop) */
  get normalizedTime(): number {
    const c = this.clip;
    return c && c.length > 0 ? this.time / c.length : 1;
  }

  /** Animator.Play(stateName, 0, normalizedTime) */
  play(stateName: string, normalizedTime = 0) {
    const id = Object.entries(this.ctrl?.states ?? {}).find(([, s]) => s.name === stateName)?.[0];
    if (id === undefined) return;
    this.enter(id, normalizedTime);
    this.holdFor = 0;
    this.apply();
  }

  /** Animator.Play(0, 0, normalizedTime): restart the default state */
  playDefault(normalizedTime = 0) {
    this.enter(this.ctrl?.defaultState ?? '', normalizedTime);
    this.holdFor = 0;
    this.apply();
  }

  setTrigger(name: string) {
    if (name in this.params) this.params[name] = 1;
  }
  resetTrigger(name: string) {
    if (name in this.params) this.params[name] = 0;
  }
  setBool(name: string, v: boolean) {
    if (name in this.params) this.params[name] = v ? 1 : 0;
  }
  setFloat(name: string, v: number) {
    if (name in this.params) this.params[name] = v;
  }

  override update(dt: number) {
    if (!this.ctrl) return;
    this.time += dt * this.speed * (this.stateDef?.speed ?? 1);
    this.holdFor = Math.max(0, this.holdFor - dt * this.speed);
    this.evaluateTransitions();
    this.apply();
  }

  private enter(id: string, normalizedTime: number) {
    this.state = id;
    this.time = normalizedTime * (this.clip?.length ?? 0);
  }

  private evaluateTransitions() {
    const st = this.stateDef;
    if (!this.ctrl || !st) return;
    const n = this.normalizedTime;
    const passes = (t: AnimTransitionDef) => {
      if (t.hasExitTime && n < t.exitTime) return false;
      if (!t.hasExitTime && !t.conditions.length) return false;
      return t.conditions.every((c) => {
        const v = this.params[c.param] ?? 0;
        switch (c.mode) {
          case ConditionMode.If: return v !== 0;
          case ConditionMode.IfNot: return v === 0;
          case ConditionMode.Greater: return v > c.threshold;
          case ConditionMode.Less: return v < c.threshold;
          case ConditionMode.Equals: return v === c.threshold;
          case ConditionMode.NotEqual: return v !== c.threshold;
          default: return false;
        }
      });
    };
    const t = this.ctrl.anyState.find(passes) ?? st.transitions.find(passes);
    if (!t) return;
    for (const c of t.conditions) if (this.ctrl.params.find((p) => p.name === c.param)?.type === ParamType.Trigger) this.params[c.param] = 0;
    this.holdKey = this.currentKey();
    this.holdFor = t.duration / 2;
    this.enter(t.dst || this.ctrl.defaultState, 0);
  }

  private currentKey(): string {
    const c = this.clip;
    if (!c || !c.keys.length) return '';
    let t = this.time;
    if (c.loop && c.length > 0) t = ((t % c.length) + c.length) % c.length;
    else t = Math.min(Math.max(t, 0), c.length);
    let key = c.keys[0][1];
    for (const [kt, k] of c.keys) {
      if (kt > t + 1e-6) break;
      key = k;
    }
    return key;
  }

  private apply() {
    const sr = this.go.sprite;
    if (!sr || !this.ctrl) return;
    const key = this.holdFor > 0 && this.holdKey ? this.holdKey : this.currentKey();
    const sp = key ? this.game.gd.spriteById.get(key) : undefined;
    if (sp) sr.sprite = sp;
  }
}

/** AnimatedEffect.Spawn: a sprite on the "Effects" layer that plays a controller once, then destroys itself. */
export class AnimatedEffect extends GameBehaviour {
  animator: Animator | null = null;
  override noSave = ['animator'];

  static spawn(game: Game, pos: V2, controller: string) {
    if (!game.gd.animations?.controllers[controller]) return;
    const go = new SimGo(game, 'AnimatedEffect', null, compose(pos.x, pos.y, 0), `effect:${game.gos.length}:${controller}`);
    go.sprite = { sprite: null, color: [1, 1, 1, 1], flipX: false, flipY: false, enabled: true, sortingLayer: 'Effects', order: 0, drawMode: 0, size: [1, 1] };
    const fx = new AnimatedEffect(go, {});
    fx.unityType = 'AnimatedEffect';
    const an = new Animator(go, { m_Controller: { guid: controller } });
    an.unityType = 'Animator';
    fx.animator = an;
    game.addBehaviour(an);
    game.addBehaviour(fx);
  }

  override update() {
    const a = this.animator;
    if (!a?.clip) return;
    if (a.normalizedTime >= 1) this.go.destroy();
  }
}
